import { useQuery } from "@apollo/client";
import { Lens } from "@hookform/lenses";
import { Box, MenuItem } from "@mui/material";
import { ReactElement } from "react";
import { useController } from "react-hook-form";

import {
  GetNgpVanSurveyQuestionWithResponsesQuery,
  GetNgpVanSurveyQuestionWithResponsesQueryVariables,
  NgpVanDatabaseModeGqlType,
  NgpVanIntegration,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { GET_NGPVAN_SURVEY_QUESTION_WITH_RESPONSES } from "~frontend/graphql/integration";

import Select from "../FormSelect";
import { RhfSelect } from "../react-hook-form-components";
import { useWritableLens } from "../react-hook-form-components/lenses";
import { RhfNgpVanActivistCodeAutocomplete } from "./NgpVanActivistCodeAutocomplete";
import { RhfNgpVanSurveyQuestionAutocomplete } from "./NgpVanSurveyQuestionAutocomplete";
import { NgpVanActionFieldValues } from "./types";

export type NgpVanTagActionType =
  | "APPLY_ACTIVIST_CODE"
  | "APPLY_RESULT_CODE"
  | "APPLY_SURVEY_RESPONSE";

function ResultCodePicker({
  lens,
  integration,
  databaseMode,
}: {
  lens: Lens<string>;
  integration: NgpVanIntegration;
  databaseMode: NgpVanDatabaseModeGqlType;
}) {
  return (
    <RhfSelect
      label="Canvass Result"
      fullWidth
      sx={{ marginBottom: 0, paddingRight: "54px" }}
      {...lens.interop()}
    >
      {integration.metadata[
        databaseMode === NgpVanDatabaseModeGqlType.MyVoters
          ? "vanMyVotersResultCodes"
          : "vanMyCampaignResultCodes"
      ].map(resultCode => (
        <MenuItem
          key={resultCode.resultCodeId}
          value={String(resultCode.resultCodeId)}
        >
          {resultCode.name}
        </MenuItem>
      ))}
    </RhfSelect>
  );
}

function ActionObjectPicker({
  integrationId,
  databaseMode,
  integration,
  actionType,
  lens,
}: {
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  integration: NgpVanIntegration;
  actionType: NgpVanTagActionType | "";
  lens: Lens<NgpVanActionFieldValues>;
}): ReactElement {
  switch (actionType) {
    case "":
      return (
        <Select label="" disabled fullWidth value="">
          <MenuItem value=""></MenuItem>
        </Select>
      );
    case "APPLY_RESULT_CODE":
      return (
        <ResultCodePicker
          lens={lens.focus("resultCodeId")}
          databaseMode={databaseMode}
          integration={integration}
        />
      );
    case "APPLY_ACTIVIST_CODE":
      return (
        <RhfNgpVanActivistCodeAutocomplete
          databaseMode={databaseMode}
          integrationId={integrationId}
          label="Activist Code"
          lens={lens.focus("activistCodeId")}
        />
      );
    case "APPLY_SURVEY_RESPONSE":
      return (
        <RhfNgpVanSurveyQuestionAutocomplete
          databaseMode={databaseMode}
          integrationId={integrationId}
          label="Survey Question"
          lens={lens}
        />
      );
  }
}

function SurveyResponsePicker({
  integrationId,
  databaseMode,
  surveyQuestionId,
  lens,
}: {
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  surveyQuestionId: number;
  lens: Lens<string>;
}): ReactElement {
  const { data, loading, error } = useQuery<
    GetNgpVanSurveyQuestionWithResponsesQuery,
    GetNgpVanSurveyQuestionWithResponsesQueryVariables
  >(GET_NGPVAN_SURVEY_QUESTION_WITH_RESPONSES, {
    variables: {
      input: {
        integrationId,
        databaseMode,
        surveyQuestionId,
      },
    },
  });

  return (
    <RhfSelect
      label="Survey Response"
      fullWidth
      {...lens.interop()}
      sx={{ paddingRight: "54px" }}
    >
      {loading && (
        <MenuItem value="" disabled>
          Loading...
        </MenuItem>
      )}
      {error && (
        <MenuItem value="" disabled>
          Error loading survey responses
        </MenuItem>
      )}
      {data?.getNgpVanSurveyQuestion?.responses.map(response => (
        <MenuItem
          key={response.surveyResponseId}
          value={String(response.surveyResponseId)}
        >
          {response.name}
        </MenuItem>
      )) ?? null}
    </RhfSelect>
  );
}

export default function RhfNgpVanActionField({
  databaseMode,
  integrationId,
  integration,
  lens,
}: {
  databaseMode: NgpVanDatabaseModeGqlType;
  integrationId: string;
  integration: NgpVanIntegration;
  lens: Lens<NgpVanActionFieldValues>;
}): ReactElement {
  const { value, setValue } = useWritableLens(lens);

  const actionTypeController = useController(
    lens.focus("actionType").interop()
  );
  const actionType = value.actionType || "";

  const surveyQuestionId: number | undefined = value.surveyQuestionId
    ? Number(value.surveyQuestionId)
    : undefined;

  return (
    <Box>
      <FlexBox>
        <Box sx={{ width: "200px" }}>
          <Select
            label="Action Type"
            fullWidth
            onChange={(event: React.ChangeEvent<any>) => {
              setValue({
                integrationIdAndDbMode: `${integrationId}|${databaseMode}`,
                actionType: event.target.value,
                activistCodeId: "",
                resultCodeId: "",
                surveyQuestionId: "",
                surveyResponseId: "",
              });
            }}
            value={actionType}
            error={Boolean(actionTypeController.fieldState.error)}
            helperText={actionTypeController.fieldState.error?.message}
          >
            <MenuItem value="APPLY_RESULT_CODE">Canvass Result</MenuItem>
            <MenuItem value="APPLY_ACTIVIST_CODE">Activist Code</MenuItem>
            <MenuItem value="APPLY_SURVEY_RESPONSE">Survey Response</MenuItem>
          </Select>
        </Box>
        <Box sx={{ flex: 1, ml: "-1px" }}>
          <ActionObjectPicker
            integrationId={integrationId}
            databaseMode={databaseMode}
            integration={integration}
            actionType={actionType}
            lens={lens}
          />
        </Box>
      </FlexBox>
      {actionType == "APPLY_SURVEY_RESPONSE" && !!surveyQuestionId && (
        <SurveyResponsePicker
          integrationId={integrationId}
          databaseMode={databaseMode}
          surveyQuestionId={surveyQuestionId}
          lens={lens.focus("surveyResponseId")}
        />
      )}
    </Box>
  );
}
